import { initializeApp } from "firebase/app";
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { updateUserFcmToken } from "./services/users";
import {
  acknowledgePushEvent,
  reportPushDiagnostics,
} from "./services/pushDiagnostics";
import { mergeSessionUserPartial } from "./shared/utils/pushHealth";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const app = initializeApp(firebaseConfig);
const messaging = getMessaging(app);

const omitUndefined = (payload) =>
  Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined),
  );

let foregroundPushListenerReady = false;

const resolvePushCopy = (payload) => {
  const title =
    payload?.notification?.title || payload?.data?.title || "Akanthas";
  const body = payload?.notification?.body || payload?.data?.body || "";
  return { title, body };
};

const FOREGROUND_NOTIFICATION_TAG = "akanthas-push";

/** Prefer SW showNotification — more reliable than `new Notification` on Android. */
const showForegroundSystemNotification = async (title, body, data = {}) => {
  if (
    typeof Notification === "undefined" ||
    Notification.permission !== "granted"
  ) {
    return;
  }

  if (!("serviceWorker" in navigator)) {
    try {
      // eslint-disable-next-line no-new
      new Notification(title, { body, data, tag: FOREGROUND_NOTIFICATION_TAG });
    } catch (error) {
      console.error("Foreground Notification failed:", error);
    }
    return;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    await registration.showNotification(title, {
      body,
      data,
      tag: FOREGROUND_NOTIFICATION_TAG,
      icon: `${window.location.origin}/icon-192x192.png`,
    });
  } catch (error) {
    console.error("Foreground SW notification failed:", error);
    try {
      // eslint-disable-next-line no-new
      new Notification(title, { body, data, tag: FOREGROUND_NOTIFICATION_TAG });
    } catch (fallbackError) {
      console.error("Foreground Notification fallback failed:", fallbackError);
    }
  }
};

/** Show push notifications while the app is open (foreground) + ack delivered. */
export const setupForegroundPushListener = () => {
  if (foregroundPushListenerReady || typeof window === "undefined") {
    return;
  }
  foregroundPushListenerReady = true;

  try {
    onMessage(messaging, (payload) => {
      void acknowledgePushEvent(payload, "delivered");

      const { title, body } = resolvePushCopy(payload);

      void showForegroundSystemNotification(title, body, {
        ...(payload?.data || {}),
        url: payload?.data?.url || "/app/notifications",
        click_action: payload?.data?.click_action || "/app/notifications",
      });

      toast.info(body ? `${title} — ${body}` : title, {
        className: "custom-toast",
      });
    });
  } catch (error) {
    console.error("Failed to setup foreground push listener:", error);
  }
};

export const requestFcmToken = async (type, { forceUpdate = false } = {}) => {
  try {
    const tokenOptions = {
      vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
    };
    // Bind FCM to the controlling app SW (has notificationclick + Firebase messaging).
    // Avoids a second firebase-messaging-sw.js registration that races on tap.
    if (
      process.env.NODE_ENV !== "development" &&
      typeof navigator !== "undefined" &&
      "serviceWorker" in navigator
    ) {
      try {
        tokenOptions.serviceWorkerRegistration =
          await navigator.serviceWorker.ready;
      } catch (error) {
        console.warn(
          "FCM: could not use app service worker registration",
          error,
        );
      }
    }

    const currentToken = await getToken(messaging, tokenOptions);

    if (currentToken) {
      const existingToken = Cookies.get("fcmToken");
      const updatePayload = omitUndefined({
        fcmToken: currentToken,
        previousFcmToken:
          existingToken && existingToken !== currentToken
            ? existingToken
            : undefined,
        receiveAnomalyPush: type === "anomaly" ? true : undefined,
        recieveCharacterizationPush:
          type === "characterization" ? true : undefined,
        recieveQualicheckPush: type === "qualicheck" ? true : undefined,
        receiveFillingPush: type === "filling_level" ? true : undefined,
      });

      // Always sync when type is set (opt-in toggle), token changed / missing cookie,
      // or login reassociation (claim exclusive ownership of this device token).
      const shouldUpdateBackend =
        forceUpdate || !existingToken || existingToken !== currentToken || type;

      if (shouldUpdateBackend) {
        const userAccess = await updateUserFcmToken(updatePayload);
        if (userAccess?.data) {
          mergeSessionUserPartial(userAccess.data);
        }
      }
      Cookies.set("fcmToken", currentToken, {
        secure: true,
        expires: 90,
        sameSite: "Lax",
      });
    }
    await reportPushDiagnostics();
    return currentToken;
  } catch (error) {
    console.error("Error getting FCM token:", error);
    await reportPushDiagnostics();
    throw error;
  }
};

export const requestPermission = async (setLoading, type) => {
  const permission = await Notification.requestPermission();
  setLoading(true);

  if (permission === "granted") {
    try {
      const token = await requestFcmToken(type);
      if (!token) {
        throw new Error(
          "Notification permission granted but no FCM token obtained",
        );
      }
      setupForegroundPushListener();
      setLoading(false);
      return "granted";
    } catch (error) {
      console.error(
        "Error during FCM subscription after permission granted:",
        error,
      );
      setLoading(false);
      return "granted_no_token";
    }
  } else {
    // Only update the preference flags, don't clear all tokens
    const updatePayload = omitUndefined({
      receiveAnomalyPush: type === "anomaly" ? false : undefined,
      recieveCharacterizationPush:
        type === "characterization" ? false : undefined,
      recieveQualicheckPush: type === "qualicheck" ? false : undefined,
      receiveFillingPush: type === "filling_level" ? false : undefined,
    });

    if (Object.keys(updatePayload).length > 0) {
      await updateUserFcmToken(updatePayload);
    }
    Cookies.remove("fcmToken");
    await reportPushDiagnostics();
  }

  setLoading(false);
  return permission;
};
